import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import "../../../utils/pdfWorker";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { HoverKarte } from "./HoverKarte";
import { DateiTypPlakette } from "../../documents/DateiTypSymbol";
import { dateiGroesse, typKurz, vorschauArt } from "./hoverVorschau";
import "./vorgangDetail.css";

/**
 * Vorschau eines Dokuments beim Überfahren (PO 30.08.2026): Bilder zeigen sich
 * als Bild, PDFs mit ihrer ersten Seite, Textdateien mit den ersten Zeilen.
 * Alles andere (docx, xlsx, zip …) nennt Typ, Größe und Datum — dort gibt es
 * nichts anzusehen, und eine leere Fläche sähe aus wie ein Ladefehler.
 *
 * Die Datei wird erst geholt, wenn die Karte wirklich aufgeht (die HoverKarte
 * ruft `inhalt` verzögert) — sonst zöge jedes Überstreichen einer Liste die
 * Dateien vom Server. Geholte Blobs bleiben in einem kleinen Cache: dieselbe
 * Zeile zweimal anzusehen kostet keinen zweiten Abruf.
 */

/**
 * Object-URLs der zuletzt angesehenen Dokumente. Bewusst gedeckelt: eine
 * Kundenakte mit hunderten Dateien hielte sonst alles im Speicher, was die
 * Maus je gestreift hat.
 */
const CACHE_MAX = 20;
const blobCache = new Map<number, string>();

function merkeBlob(id: number, url: string): void {
    blobCache.set(id, url);
    while (blobCache.size > CACHE_MAX) {
        const [aeltester, alteUrl] = blobCache.entries().next().value as [number, string];
        blobCache.delete(aeltester);
        URL.revokeObjectURL(alteUrl);
    }
}

const Infokarte: React.FC<{ dokument: DocumentDto; hinweis?: string }> = ({ dokument, hinweis }) => {
    const groesse = dateiGroesse(dokument.fileSize);
    const datum = dokument.uploadedAt
        ? new Date(dokument.uploadedAt).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
          })
        : "";
    return (
        <div className="vgd-hover-info">
            <div className="vgd-hover-titel">
                <DateiTypPlakette fileName={dokument.fileName} mimeType={dokument.mimeType} className="vgd-hover-dt" />
                {dokument.fileName}
            </div>
            <div className="vgd-hover-meta">
                {[typKurz(dokument.fileName, dokument.mimeType), groesse, datum].filter(Boolean).join(" · ")}
            </div>
            {dokument.category && <span className="vgd-minibadge gold">{dokument.category}</span>}
            {hinweis && <div className="vgd-hover-hinweis">{hinweis}</div>}
        </div>
    );
};

const Inhalt: React.FC<{ dokument: DocumentDto }> = ({ dokument }) => {
    const art = vorschauArt(dokument.fileName, dokument.mimeType);
    const [url, setUrl] = useState<string | null>(() => blobCache.get(dokument.id) ?? null);
    const [text, setText] = useState<string | null>(null);
    const [fehler, setFehler] = useState(false);
    const [laedt, setLaedt] = useState(false);

    const holen = useCallback(async () => {
        if (art === "keine" || url || text) return;
        setLaedt(true);
        try {
            const blob = await documentService.fetchBlob(dokument.id);
            if (art === "text") {
                setText((await blob.text()).slice(0, 1200));
            } else {
                const objektUrl = URL.createObjectURL(blob);
                merkeBlob(dokument.id, objektUrl);
                setUrl(objektUrl);
            }
        } catch (err) {
            console.error("[DokumentHoverVorschau] Datei konnte nicht geladen werden", err);
            setFehler(true);
        } finally {
            setLaedt(false);
        }
    }, [art, dokument.id, url, text]);

    useEffect(() => {
        void holen();
    }, [holen]);

    if (art === "keine") return <Infokarte dokument={dokument} />;
    if (fehler) return <Infokarte dokument={dokument} hinweis="Vorschau konnte nicht geladen werden." />;
    if (laedt || (!url && !text)) {
        return (
            <div className="vgd-hover-info">
                <div className="vgd-hover-titel">
                    <DateiTypPlakette fileName={dokument.fileName} mimeType={dokument.mimeType} className="vgd-hover-dt" />
                    {dokument.fileName}
                </div>
                <div className="vgd-hover-meta">Vorschau wird geladen…</div>
            </div>
        );
    }

    return (
        <div className="vgd-hover-vorschau">
            {art === "bild" && url && (
                <img src={url} alt="" className="vgd-hover-bild" onError={() => setFehler(true)} />
            )}
            {art === "pdf" && url && (
                <div className="vgd-hover-pdf">
                    <Document file={url} loading="" error="" noData="">
                        <Page pageNumber={1} width={300} renderTextLayer={false} renderAnnotationLayer={false} />
                    </Document>
                </div>
            )}
            {art === "text" && text != null && <pre className="vgd-hover-text">{text}</pre>}
            <div className="vgd-hover-fuss">
                <DateiTypPlakette fileName={dokument.fileName} mimeType={dokument.mimeType} className="vgd-hover-dt" />
                {dokument.fileName}
            </div>
        </div>
    );
};

export const DokumentHoverVorschau: React.FC<{
    dokument: DocumentDto;
    ankerClass?: string;
    children: React.ReactNode;
}> = ({ dokument, ankerClass, children }) => (
    <HoverKarte breite={332} ankerClass={ankerClass} inhalt={() => <Inhalt dokument={dokument} />}>
        {children}
    </HoverKarte>
);
